import GooglePayButton from '@google-pay/button-react';
import Spinner from 'components/spinner/Spinner';
import withContext from 'hoc/withContext';
import withLocaleContext from 'hoc/withLocaleContext';
import React, { PureComponent } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { updatePayment } from 'services/Api';
import { PaymentPartial } from 'types/Payment';
import { Buffer } from 'buffer';

import { FlexContainer } from './PaymentGPayButton.styles';
import withGooglePayConfigurationContext from '../../../hoc/WithGooglePayConfigurationContext';
import { IGooglePayConfigurationContext } from '../../../types/GooglePayConfigurationContext';
import { trimLanguageTag } from '../../../helpers/commonHelper';

interface IPaymentGPayButton
  extends RouteComponentProps,
    Partial<IContext>,
    Partial<ILocaleContext>,
    Partial<IGooglePayConfigurationContext> {
  isVisible: boolean;
  payment: PaymentPartial;
  paymentId: string;
  paymentMethodId?: string;
  paymentMethodType?: string;
  setOrderDescriptionVisibility?: (visible: boolean) => void;
}

class PaymentGPayButton extends PureComponent<
  IPaymentGPayButton & Record<string, unknown>,
  { preTransition: boolean; locale?: string }
> {
  gPayEnv = process.env.REACT_APP_GPAY_ENVIRONMENT as google.payments.api.Environment;

  constructor(props) {
    super(props);
    this.state = {
      preTransition: true,
    };
  }

  componentDidMount() {
    requestAnimationFrame(() => {
      this.setState({ preTransition: false });
    });
  }

  handleClick = async (token: string): Promise<google.payments.api.PaymentAuthorizationResult> => {
    const { payment } = this.props;

    try {
      const {
        paymentId,
        paymentMethodId,
        pmpt,
        locale,
        history: { push },
        location: { pathname, search },
      } = this.props;
      const selectedPaymentMethod = paymentMethodId || payment.selectedPaymentMethodId;
      const selectedPaymentMethodPType = pmpt || payment.selectedPmpt;

      if (!selectedPaymentMethod) {
        throw new Error('Payment method not selected');
      }

      const { redirectUrl, status } = await updatePayment(paymentId, {
        locale,
        paymentMethodId: selectedPaymentMethod,
        paymentMethodToken: Buffer.from(JSON.stringify(token)).toString('base64'),
        pmpt: selectedPaymentMethodPType,
      });
      setTimeout(() => {
        if (redirectUrl) {
          window.location.replace(redirectUrl);
        } else {
          push(`${pathname}/callback${search}&selectedPaymentMethodType=GOOGLE_PAY`, { status });
        }
      }, 1000);
    } catch (error) {
      window.location.reload();
      this.setState(() => {
        throw error;
      });
    }
    return { transactionState: 'SUCCESS' };
  };

  handlePaymentAuthorized = (paymentData: google.payments.api.PaymentData) =>
    new Promise<google.payments.api.PaymentAuthorizationResult>(resolve => {
      this.handleClick(paymentData.paymentMethodData.tokenizationData.token).then(result => {
        resolve(result);
      });
    });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleOnCancel = (reason: google.payments.api.PaymentsError) => {
    const { setOrderDescriptionVisibility } = this.props;
    if (setOrderDescriptionVisibility) setOrderDescriptionVisibility(true);
  };

  render() {
    const { isVisible, googlePayConfiguration, locale } = this.props;
    const { preTransition } = this.state;

    return (
      <>
        {isVisible && (
          <FlexContainer preTransition={preTransition} className="google-pay-button-wrapper">
            {googlePayConfiguration ? (
              <GooglePayButton
                className="gpay-button"
                environment={this.gPayEnv}
                buttonSizeMode="fill"
                buttonRadius={11}
                style={{ width: '100%', padding: '0', boxShadow: 'none', marginBottom: '0.4rem', height: '2.5rem' }}
                buttonBorderType="no_border"
                onPaymentAuthorized={this.handlePaymentAuthorized}
                onCancel={this.handleOnCancel}
                paymentRequest={googlePayConfiguration}
                buttonLocale={locale && trimLanguageTag(locale)}
              />
            ) : (
              <Spinner isLoading />
            )}
          </FlexContainer>
        )}
      </>
    );
  }
}

export default withLocaleContext(withGooglePayConfigurationContext(withContext(withRouter(PaymentGPayButton))));
